<template>
    <div class="json-formatter">
        <h3>JSON 校验与格式化</h3>

        <!-- 输入框 -->
        <textarea
                v-model="jsonText"
                placeholder='请输入 JSON 数据，例如：{"name": "张三", "age": 30}'
                @input="validateJson"
                class="json-input"
        ></textarea>

        <!-- 操作按钮 -->
        <div class="action-buttons">
            <button @click="formatJson">美化 (格式化)</button>
            <button @click="minifyJson">压缩 (转为一行)</button>
            <button @click="downloadJson" :disabled="!isValid">⬇ 下载 JSON</button>
        </div>

        <!-- 错误提示 -->
        <div v-if="error" class="error-message">
            ❌ 校验失败：{{ error }}
        </div>

        <!-- 格式化后预览 -->
        <div v-if="formattedJson && isValid" class="preview">
            <h4>格式化预览：</h4>
            <pre><code v-html="highlightJson(formattedJson)"></code></pre>
        </div>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>📋 JSON 校验与格式化工具</h2>

            <div class="tool-content-intro">
                <p>开发者必备的 JSON 处理工具，支持语法校验、美化格式化、一键压缩。带语法高亮显示，快速定位语法错误，提升开发效率。</p>
            </div>

            <div class="content-section">
                <h3><span class="icon">✨</span> 功能特点</h3>
                <ul>
                    <li><strong>实时校验</strong>：输入时自动检测 JSON 语法错误</li>
                    <li><strong>一键美化</strong>：将压缩的 JSON 格式化为易读格式</li>
                    <li><strong>一键压缩</strong>：去除多余空格，减小体积</li>
                    <li><strong>语法高亮</strong>：不同数据类型用颜色区分</li>
                    <li><strong>一键下载</strong>：导出为 .json 文件</li>
                </ul>
            </div>

            <div class="content-section">
                <h3><span class="icon">📝</span> 使用教程</h3>
                <ol>
                    <li>将 JSON 数据粘贴到输入框</li>
                    <li>系统自动校验语法，错误会红色提示</li>
                    <li>点击「美化」格式化，或「压缩」去除空格</li>
                    <li>点击「下载 JSON」保存文件</li>
                </ol>
            </div>

            <div class="content-section">
                <h3><span class="icon">💡</span> 常见错误排查</h3>
                <div class="faq-grid">
                    <div class="faq-item">
                        <p>Unexpected token</p>
                        <p>检查是否有中文标点（，。）替换为英文 (, .)</p>
                    </div>
                    <div class="faq-item">
                        <p>Unexpected end of JSON</p>
                        <p>JSON 未闭合，检查括号是否成对</p>
                    </div>
                    <div class="faq-item">
                        <p>Expected ',' or '}'</p>
                        <p>缺少逗号或多余逗号</p>
                    </div>
                </div>
            </div>

            <div class="privacy-box">
                <span class="icon">🔒</span>
                <p><strong>隐私说明</strong>：纯前端处理，JSON 数据不会上传到服务器。</p>
            </div>
        </div>
    </div>
</template>

<script>
    import seoMixin from '../js/seo-mixin';
    export default {
        name: 'JsonFormatter',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: 'JSON校验与格式化工具 - FreeToolBox',
                seoDescription: '免费在线JSON校验与格式化工具，支持JSON语法校验、美化格式化、一键压缩。带语法高亮显示，浏览器本地处理，开发必备工具。',
                seoKeywords: 'json格式化,json校验,json美化,json压缩,json在线工具',
                jsonText: '{\n  "name": "张三",\n  "age": 30,\n  "active": true\n}',
                formattedJson: '',
                error: '',
                isValid: false,
            };
        },
        methods: {
            validateJson() {
                const text = this.jsonText.trim();
                if (!text) {
                    this.error = '请输入 JSON 内容';
                    this.isValid = false;
                    this.formattedJson = '';
                    return;
                }

                try {
                    const parsed = JSON.parse(text);
                    this.formattedJson = JSON.stringify(parsed, null, 2); // 美化
                    this.error = '';
                    this.isValid = true;
                } catch (e) {
                    this.error = e.message;
                    this.isValid = false;
                    this.formattedJson = '';
                }
            },

            formatJson() {
                const text = this.jsonText.trim();
                if (!text) return;

                try {
                    const parsed = JSON.parse(text);
                    this.jsonText = JSON.stringify(parsed, null, 2);
                    this.validateJson(); // 重新校验
                } catch (e) {
                    this.error = '无法格式化：' + e.message;
                }
            },

            minifyJson() {
                const text = this.jsonText.trim();
                if (!text) return;

                try {
                    const parsed = JSON.parse(text);
                    this.jsonText = JSON.stringify(parsed);
                    this.validateJson(); // 重新校验
                } catch (e) {
                    this.error = '无法压缩：' + e.message;
                }
            },

            downloadJson() {
                if (!this.isValid || !this.formattedJson) return;

                const blob = new Blob([this.formattedJson], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'data.json';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            },

            // 简单的 JSON 语法高亮（颜色）
            highlightJson(jsonStr) {
                return jsonStr
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(
                        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\\-]?\d+)?)/g,
                        (match) => {
                            let cls = 'number';
                            if (/^"/.test(match)) {
                                cls = /:$/.test(match) ? 'key' : 'string';
                            } else if (/true|false/.test(match)) {
                                cls = 'boolean';
                            } else if (/null/.test(match)) {
                                cls = 'null';
                            }
                            return `<span class="${cls}">${match}</span>`;
                        }
                    );
            },
        },
        mounted() {
            this.validateJson(); // 初始化校验
        },
    };
</script>

<style scoped>
    .json-formatter {
        padding: 20px;
        border: 1px solid #ccc;
        border-radius: 8px;
        max-width: 600px;
        margin: 40px auto;
        font-family: Arial, sans-serif;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }

    h3 {
        text-align: center;
        color: #333;
        margin-bottom: 20px;
    }

    .json-input {
        width: 100%;
        height: 180px;
        padding: 12px;
        font-size: 14px;
        font-family: 'Courier New', monospace;
        border: 1px solid #ddd;
        border-radius: 4px;
        outline: none;
        resize: vertical;
    }

    .json-input:focus {
        border-color: #409eff;
        box-shadow: 0 0 5px rgba(64, 158, 255, 0.2);
    }

    .action-buttons {
        display: flex;
        gap: 10px;
        margin: 15px 0;
        flex-wrap: wrap;
    }

    .action-buttons button {
        flex: 1;
        min-width: 100px;
        padding: 10px;
        font-size: 14px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.3s;
    }

    .action-buttons button:nth-child(1) {
        background-color: #409eff;
        color: white;
    }

    .action-buttons button:nth-child(2) {
        background-color: #909399;
        color: white;
    }

    .action-buttons button:nth-child(3) {
        background-color: #28a745;
        color: white;
    }

    .action-buttons button:hover {
        opacity: 0.9;
    }

    .action-buttons button:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }

    .error-message {
        color: #d93025;
        font-size: 14px;
        margin: 10px 0;
        padding: 10px;
        background-color: #ffebee;
        border-radius: 4px;
        border: 1px solid #ffcdd2;
    }

    .preview {
        margin-top: 20px;
        text-align: left;
    }

    .preview h4 {
        color: #333;
        margin-bottom: 10px;
    }

    .preview pre {
        background-color: #f5f5f5;
        border: 1px solid #ddd;
        border-radius: 4px;
        padding: 15px;
        margin: 0;
        overflow-x: auto;
        font-family: 'Courier New', monospace;
        font-size: 13px;
        line-height: 1.5;
    }

    .preview code .string { color: #22863a; }
    .preview code .number { color: #005cc5; }
    .preview code .boolean { color: #d73a49; }
    .preview code .null { color: #6f42c1; }
    .preview code .key { color: #6f42c1; font-weight: bold; }
    /* 内容说明区样式 */
    .tool-content {
        max-width: 900px;
        margin: 50px auto;
        padding: 0 20px;
        text-align: left;
    }

    .tool-content h2 {
        font-size: 1.8em;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .tool-content h2::before {
        content: '';
        display: inline-block;
        width: 5px;
        height: 32px;
        background: linear-gradient(180deg, #2196f3, #64b5f6);
        border-radius: 3px;
    }

    .tool-content-intro {
        background: linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%);
        border-radius: 16px;
        padding: 25px 30px;
        margin-bottom: 30px;
        border: 1px solid #bbdefb;
        box-shadow: 0 4px 15px rgba(33, 150, 243, 0.08);
    }

    .tool-content-intro p {
        color: #1565c0;
        font-size: 15px;
        line-height: 1.9;
        margin: 0;
    }

    .content-section {
        background: #fff;
        border-radius: 16px;
        padding: 25px 30px;
        margin-bottom: 20px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
        border: 1px solid #f0f0f0;
        transition: box-shadow 0.3s ease;
    }

    .content-section:hover {
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    }

    .content-section h3 {
        color: #333;
        font-size: 1.15em;
        margin: 0 0 15px 0;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .content-section h3 .icon {
        font-size: 1.2em;
    }

    .content-section ul, .content-section ol {
        margin: 0;
        padding-left: 24px;
    }

    .content-section li {
        color: #555;
        line-height: 2;
        margin-bottom: 8px;
        position: relative;
    }

    .content-section ul li::marker {
        color: #2196f3;
    }

    .content-section ol li::marker {
        color: #2196f3;
        font-weight: 600;
    }

    .content-section strong {
        color: #333;
    }

    .faq-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 15px;
    }

    .faq-item {
        background: #fafbfc;
        border-radius: 12px;
        padding: 18px 20px;
        border: 1px solid #eee;
    }

    .faq-item p {
        margin: 0;
        font-family: 'Courier New', monospace;
        font-size: 13px;
        color: #e91e63;
        background: #fce4ec;
        padding: 8px 12px;
        border-radius: 6px;
        margin-bottom: 8px;
    }

    .faq-item p:last-child {
        font-family: inherit;
        font-size: 14px;
        color: #666;
        background: #f5f5f5;
    }

    .privacy-box {
        background: linear-gradient(135deg, #f0f9f0 0%, #e8f5e8 100%);
        border: 1px solid #c8e6c9;
        border-radius: 12px;
        padding: 18px 25px;
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }

    .privacy-box .icon {
        font-size: 1.5em;
        flex-shrink: 0;
    }

    .privacy-box p {
        margin: 0;
        color: #2e7d32;
        line-height: 1.7;
    }
</style>